import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import { useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import {
  API_BASE_URL,
  DEFAULT_PAGE_DESCRIPTION,
  DEFAULT_PAGE_TITLE,
  DEFAULT_PER_PAGE,
} from "../../utilities/constants";
import { NavLink, useLocation, useNavigate } from "react-router";
import Pagination from "../../components/ui/Pagination";
import { useUI } from "../../context/UI";
import useQueryParams from "../../hooks/useQueryParams";
import { dobToAge, formatHeightUnit, handleApiCall, jsonToQueryParams } from "../../utilities/helper";
import { FaCheckCircle, FaClock, FaTimesCircle } from "react-icons/fa";
import TableFilter from "./TableFilter";
import ButtonText from "../../components/ui/button/ButtonText";

const CustomerPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(false);
  const location = useLocation();
  const { queryParams, updateQueryParam } = useQueryParams();
  const pageNo = queryParams.get("page") || 0;
  const pageSize = queryParams.get("pageSize") || DEFAULT_PER_PAGE;
  const sortOrder = queryParams.get("sortOrder") || "DESC";
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const isChecked = (ids) => selectedIds.includes(ids);
  const isDeleteDisabled = selectedIds?.length === 0;
  const { setSegmentTitle } = useUI();
  const [controller, setController] = useState(null);

  const callListApi = async () => {
    controller?.abort();
    const newController = new AbortController();
    setController(newController);
    const { signal } = newController;
    setLoading(true);
    try {
      const query = jsonToQueryParams({
        keyword: queryParams.get("search") || sessionStorage.getItem("customer_search") || "",
        age: queryParams.get("age") || sessionStorage.getItem("customer_age") || "",
        gender: queryParams.get("gender") || sessionStorage.getItem("customer_gender") || "",
      });
      const response = await handleApiCall(
        `${API_BASE_URL}/api/1.0.0/customer?page=${pageNo}&size=${pageSize}&sortBy=createdAt&direction=${sortOrder}&${query}`,
        {
          method: "GET"
        },
        null,
        signal
      );
      if (response.responseStatus == true) {
        setData(response.data);
        return true
      } else {
        setData(null);
      }
      if (response.responseCode !== 299) {
        setLoading(false);
      }
    } catch (e) {
      console.error("Error", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    callListApi();
    setSegmentTitle("User");
    setSelectedIds([]);
    setAllSelected(false);
  }, [location.search]);

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all
      setSelectedIds([]);
      setAllSelected(false);
    } else {
      // Select all customer IDs
      const allIds = data?.data?.map((user) => user.id) || [];
      setSelectedIds(allIds);
      setAllSelected(true);
    }
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) => {
      let updated;
      if (prev.includes(id)) {
        updated = prev.filter((item) => item !== id);
      } else {
        updated = [...prev, id];
      }

      setAllSelected(updated.length === data?.data?.length);

      return updated;
    });
  };

  const resetFilters = () => {
    updateQueryParam("search", "");
    updateQueryParam("age", "");
    updateQueryParam("gender", "");
    updateQueryParam("page", 0);
    updateQueryParam("pageSize", DEFAULT_PER_PAGE);
    setSelectedIds([]);
    setAllSelected(false);
  };

  return (
    <>
      <PageMeta
        title={`Customers - ${DEFAULT_PAGE_TITLE}`}
        description={`Customers - ${DEFAULT_PAGE_DESCRIPTION}`}
      />
      <PageBreadcrumb
        pageTitle="Customer"
      />
      <div className="flex justify-between items-center mb-4">
        <TableFilter resetFilters={resetFilters} />
        <NavLink to="/template" state={{ selectedIds }}>
          <ButtonText
            title='Notify'
            disabled={isDeleteDisabled}
          />
        </NavLink>
      </div>
      {loading ? (
        <div className="flex justify-center items-center">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="relative overflow-x-auto sm:rounded-lg">
            <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400 rounded-lg">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                      onClick={(e) => e.stopPropagation()}
                      className="appearance-none w-5 h-5 cursor-pointer rounded
                        border border-gray-300
                        checked:bg-brand-500 checked:border-brand-500
                        focus:ring-2 focus:ring-brand-200"
                    />
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Gender
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Age
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Weight
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Height
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.data?.length > 0 ? data?.data.map((user, index) => (
                  <tr
                    key={index}
                    // onClick={() => navigate(`/customers-details/${user?.id}`)}
                    className="border-b dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    <td className="px-6 py-4 font-medium whitespace-nowrap cursor-pointer">
                      <span
                      >
                        <input
                          type="checkbox"
                          className="appearance-none w-5 h-5 cursor-pointer rounded
                          border border-gray-300
                          checked:bg-brand-500 checked:border-brand-500
                          focus:ring-2 focus:ring-brand-200 checked:hover:bg-brand-500"
                          checked={isChecked(user?.id)}
                          onChange={() => handleCheckboxChange(user?.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </span>
                    </td>

                    <td
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                    >
                      <div className="flex items-center gap-2">
                        {user?.photoSignedUrl ? (
                          <img
                            src={user?.photoSignedUrl}
                            alt={user?.firstname || "User"}
                            className="w-8 h-8 rounded-full object-fill"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-sm font-semibold text-gray-700">
                            {user?.firstname ? user?.firstname.charAt(0).toUpperCase() : "-"}
                          </div>
                        )}
                        <span
                          className="max-w-[200px] block truncate"
                          title={`${user?.firstname || ""} ${user?.lastname || ""}`.trim()}
                        >
                          {(() => {
                            const fullName = `${user?.firstname || ""} ${user?.lastname || ""}`.trim();
                            return fullName.length > 20 ? fullName.slice(0, 20) + "…" : fullName;
                          })()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className="max-w-[120px] block truncate"
                        title={`${user?.email || ""}`.trim()}
                      >
                        {(() => {
                          const Emails = `${user?.email || ""}`.trim();
                          return Emails.length > 25 ? Emails.slice(0, 25) + "…" : Emails;
                        })()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user?.gender ? user?.gender : "-"}
                    </td>
                    <td className="px-6 py-4">
                      {dobToAge(user?.dob) || "0"} <span className="text-sm font-normal">YEARS</span>
                    </td>
                    <td className="px-6 py-4">
                      {user?.weight || 0} <span className="text-sm font-normal">{user?.weightUnit || "KG"}</span>
                    </td>
                    <td className="px-6 py-4">
                      {user?.height || 0} <span className="text-sm font-normal">{formatHeightUnit(data?.profile?.profile?.heightUnit) || "CM"}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className={`flex items-center gap-2 text-xs px-3 py-1 rounded-full w-fit
                            ${user?.statusId === 1
                            ? "bg-green-100 text-green-700"
                            : user?.statusId === 2
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                      >
                        {user?.statusId === 1 && <FaCheckCircle className="text-green-500" />}
                        {user?.statusId === 2 && <FaTimesCircle className="text-red-500" />}
                        {user?.statusId === 15 && <FaClock className="text-yellow-600" />}
                        {user?.statusId === 1 ? "Active" : user?.statusId === 2 ? "Inactive" : "Deleted"}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <NavLink
                        to={`/customers-details/${user?.id}`}
                      // onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="bg-yellow-600 text-white px-6 py-2 rounded-md hover:bg-yellow-700 transition"
                        >
                          View
                        </button>
                      </NavLink>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="9" className="px-4 py-4 text-center text-gray-500 dark:text-gray-400">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className='flex justify-center items-center'>
        <Pagination
          pageParam="page"
          currentPage={data?.pageNumber + 1}
          totalElements={data?.totalElements}
        />
      </div>
    </>
  );
};
export default CustomerPage;
